import React, { useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Home, Delete } from '@mui/icons-material';
import { useDarkMode } from '../context/DarkModeContext';
import TextInputArea from '../components/TextInputArea';
import JsonDiffReact from 'jsondiffpatch-react';
import Footer from '../components/Footer';

const JsonComparePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [leftJson, setLeftJson] = useState('');
  const [rightJson, setRightJson] = useState('');
  const [error, setError] = useState('');
  const [showDiff, setShowDiff] = useState(false);
  const [parsedLeft, setParsedLeft] = useState(null);
  const [parsedRight, setParsedRight] = useState(null);
  const [isIdentical, setIsIdentical] = useState(false);

  const handleCompare = () => {
    setError('');
    setShowDiff(false);
    setIsIdentical(false);
    try {
      if (!leftJson.trim() || !rightJson.trim()) {
        setError('Please enter JSON in both boxes.');
        return;
      }
      const left = JSON.parse(leftJson);
      const right = JSON.parse(rightJson);
      setParsedLeft(left);
      setParsedRight(right);
      
      // Check if JSONs are identical
      const leftStr = JSON.stringify(left);
      const rightStr = JSON.stringify(right);
      if (leftStr === rightStr) {
        setIsIdentical(true);
      } else {
        setShowDiff(true);
      }
    } catch {
      setError('Invalid JSON in one or both boxes.');
    }
  };

  const handleClear = () => {
    setLeftJson('');
    setRightJson('');
    setShowDiff(false);
    setError('');
    setParsedLeft(null);
    setParsedRight(null);
    setIsIdentical(false);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: theme.palette.background.default, 
      color: theme.palette.text.primary, 
      p: 3,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Dark mode toggle in top right corner */}
      <button
        onClick={toggleDarkMode}
        className="dark-mode-toggle"
        style={{ 
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          color: '#fff',
          fontSize: '1rem',
          padding: '8px 16px',
          borderRadius: '4px',
          backgroundColor: '#388e3c',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '40px'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#2e7d32'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#388e3c'}
      >
        <span className="dark-mode-text">
          {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
        </span>
        <span className="dark-mode-icon">
          {darkMode ? '☀️' : '🌙'}
        </span>
      </button>

      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Button
          startIcon={<Home />}
          onClick={() => navigate('/')}
          sx={{ 
            color: '#fff',
            textDecoration: 'none',
            fontSize: '1rem',
            padding: '8px 16px',
            borderRadius: '4px',
            bgcolor: '#388e3c',
            textTransform: 'none',
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              bgcolor: '#2e7d32'
            }
          }}
        >
          Home
        </Button>
        <Typography variant="h4" sx={{ color: darkMode ? '#4CAF50' : '#006400', fontWeight: 'bold', textAlign: 'center', flexGrow: 1, fontSize: { xs: '2rem', sm: '2.5rem' } }}>
          JSON Compare
        </Typography>
      </Box>
      {/* Main Content */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <TextInputArea
            label={<span style={{ color: darkMode ? '#81c784' : '#388e3c', fontWeight: 'bold' }}>Original JSON</span>}
            value={leftJson}
            onChange={setLeftJson}
            placeholder="Paste your first JSON here..."
            onCopy={() => handleCopy(leftJson)}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextInputArea
            label={<span style={{ color: darkMode ? '#81c784' : '#388e3c', fontWeight: 'bold' }}>Modified JSON</span>}
            value={rightJson}
            onChange={setRightJson}
            placeholder="Paste your second JSON here..."
            onCopy={() => handleCopy(rightJson)}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'center' }}>
        <Button variant="contained" onClick={handleCompare} sx={{ bgcolor: '#388e3c', color: '#fff', fontSize: '1rem', textTransform: 'none', minWidth: 140, '&:hover': { bgcolor: '#2e7d32' } }}>
          Compare
        </Button>
        <Button variant="outlined" onClick={handleClear} startIcon={<Delete />} sx={{ color: '#388e3c', borderColor: '#388e3c', background: 'transparent', textTransform: 'none', minWidth: 140, fontSize: '1rem', '&:hover': { background: 'rgba(56, 142, 60, 0.08)', borderColor: '#2e7d32', color: '#2e7d32' } }}>
          Clear
        </Button>
      </Box>
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      {isIdentical && (
        <Box sx={{ 
          mt: 2, 
          background: darkMode ? '#1b5e20' : '#e8f5e8', 
          borderRadius: 2, 
          p: 3,
          textAlign: 'center',
          border: `2px solid ${darkMode ? '#388e3c' : '#388e3c'}`
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: darkMode ? '#81c784' : '#2e7d32', 
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}
          >
            ✅ JSONs are identical!
          </Typography>
          <Typography 
            sx={{ 
              color: darkMode ? '#a5d6a7' : '#388e3c',
              mt: 1,
              fontSize: '1rem'
            }}
          >
            Both JSON objects have the same structure and values.
          </Typography>
        </Box>
      )}
      {showDiff && parsedLeft && parsedRight && (
        <Box sx={{ mt: 2, background: darkMode ? '#23272e' : '#f8f8f8', borderRadius: 2, p: 2 }}>
          <JsonDiffReact left={parsedLeft} right={parsedRight} show={true} annotated={false} />
        </Box>
      )}
      <Footer />
    </Box>
  );
};

export default JsonComparePage; 